/**
 * SampleBank. Loads and caches Tone.Player instances for a set of audio URLs.
 * Provides indexed access and a dispose method to release all players.
 * Never touches three.js, PointerEvents, or DOM.
 */

import * as Tone from 'tone';

const PLAYER_LOAD_TIMEOUT_MS = 5_000;

// ─── SampleBank ──────────────────────────────────────────────────────────

export class SampleBank {
  /** Internal map from index → Player. */
  private players: Map<number, Tone.Player> = new Map();

  /** Number of loaded players. */
  get size(): number {
    return this.players.size;
  }

  /**
   * Load players for each URL. Resolves once all buffers have actually decoded
   * (not just when Player instances are constructed — that distinction matters,
   * because injectOneshot → player.start() throws if the buffer hasn't loaded).
   *
   * Tone v15: passing `onload` to the Player constructor invokes the callback
   * once the buffer has decoded. We wrap that in a per-player promise and
   * await all of them. Errors per-player are logged but don't reject the whole
   * load — a single 404 oneshot shouldn't kill the entire mode.
   */
  async load(urls: string[]): Promise<void> {
    const tries = urls.map(
      (url, i) =>
        new Promise<void>((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            resolve();
          };
          const timeout = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn(`SampleBank: oneshot ${i} (${url}) did not load within ${PLAYER_LOAD_TIMEOUT_MS}ms`);
            finish();
          }, PLAYER_LOAD_TIMEOUT_MS);
          const player = new Tone.Player({
            url,
            onload: () => finish(),
            onerror: (err) => {
              // eslint-disable-next-line no-console
              console.warn(`SampleBank: oneshot ${i} (${url}) failed to load`, err);
              finish();
            },
          });
          this.players.set(i, player);
          // Test mocks construct Players with `loaded = true` synchronously and
          // never invoke onload. Resolve immediately in that case so tests don't
          // hang. Real Tone Players have loaded=false at construction and the
          // onload callback above takes over.
          if ((player as unknown as { loaded?: boolean }).loaded) {
            finish();
          }
        }),
    );
    await Promise.all(tries);
  }

  /** Return the player at the given index, or undefined if out of range. */
  get(index: number): Tone.Player | undefined {
    return this.players.get(index);
  }

  /** Connect every loaded player to the given audio node. */
  connect(destination: Tone.ToneAudioNode): void {
    for (const player of this.players.values()) {
      player.connect(destination);
    }
  }

  /** Dispose all players and clear the bank. Returns a resolved promise for chaining. */
  dispose(): Promise<void> {
    for (const player of this.players.values()) {
      player.dispose();
    }
    this.players.clear();
    return Promise.resolve();
  }
}
